import { DateRange } from '@mui/x-date-pickers-pro'
import { format } from 'date-fns'
import { AbsenceReasonTypesEnum, AbsenceTypesEnum } from '../Absence/types'
import { absenceFormLabels, COMPASSIONATE_HEADERS } from '../../utils/constants'
import { AbsenceDayRequest, DailyOSPDictionary } from '../../types/absence'

export const IsAbsentFromBetweenShiftTime = (
  absentFrom: Date | null,
  shiftStart: Date | undefined,
  shiftEnd: Date | undefined
): boolean => {
  if (!absentFrom) return false

  if (!shiftStart) return false

  if (!shiftEnd) return false

  return absentFrom >= shiftStart && absentFrom <= shiftEnd
}

export const buildPayApprovalText = (displayHours: number): string => {
  if (displayHours > 0) {
    return `I confirm ${displayHours} ${displayHours === 1 ? 'hour' : 'hours'} will be sent for payment approval. A Discretionary Pay request will be created in 'My Actions'`
  }
  return 'I confirm no hours will be sent for payment approval. No Discretionary Pay request will be created.'
}

export const CreateShiftTime = (shiftDate: Date | null, shiftTime: string): Date | undefined => {
  if (!shiftTime) return undefined

  if (!shiftDate) return undefined

  const time = shiftTime.split('.')

  if (time.length !== 2) return undefined

  return new Date(
    shiftDate.getFullYear(),
    shiftDate.getMonth(),
    shiftDate.getDate(),
    Number(time[0]),
    Number(time[1]),
    0
  )
}

export const getShiftDateRange = (dateRange: DateRange<Date>, shiftDescription: string) => {
  const times = shiftDescription.split(' ')[0].split('-')

  if (times.length !== 2) {
    return undefined
  }

  const startTime = CreateShiftTime(dateRange[0], times[0])
  let endTime = CreateShiftTime(dateRange[0], times[1])

  // Handle overnight shifts: if end time is before start time, it's the next day
  if (startTime && endTime && endTime < startTime) {
    const nextDay = new Date(endTime)
    nextDay.setDate(nextDay.getDate() + 1)
    endTime = nextDay
  }

  return [startTime, endTime]
}

export const calculateManAuthDays = (
  selectedDaysArray: { day: Date | string; hours: number; checked: boolean }[] | null,
  approvedHours: number,
  exceededHoursPerDay: Record<string, number>
): { day: string; hours: number }[] => {
  if (!selectedDaysArray) {
    return []
  }

  // Sort days chronologically
  const sortedDays = [...selectedDaysArray]
    .filter(day => day.checked)
    .sort((a, b) => {
      const dateA = typeof a.day === 'string' ? new Date(a.day) : a.day
      const dateB = typeof b.day === 'string' ? new Date(b.day) : b.day
      return dateA.getTime() - dateB.getTime()
    })

  // Calculate total man auth needed across all days
  const totalManAuthNeeded = sortedDays.reduce((sum, day) => {
    const dateStr = typeof day.day === 'string' ? day.day : format(day.day, 'yyyy-MM-dd')
    const exceeded = exceededHoursPerDay[dateStr] ?? day.hours
    return sum + exceeded
  }, 0)

  // Limit to approved hours
  const effectiveApprovedHours = Math.min(approvedHours, totalManAuthNeeded)

  if (effectiveApprovedHours <= 0) {
    return []
  }

  // Distribute approved hours day by day
  const manAuthDays: { day: string; hours: number }[] = []
  let hoursRemaining = effectiveApprovedHours

  for (const day of sortedDays) {
    if (hoursRemaining <= 0) break

    const dateStr = typeof day.day === 'string' ? day.day : format(day.day, 'yyyy-MM-dd')
    const hoursNeededForThisDay = exceededHoursPerDay[dateStr] ?? day.hours

    if (hoursNeededForThisDay > 0) {
      const hoursForThisDay = Math.min(hoursNeededForThisDay, hoursRemaining)
      
      manAuthDays.push({
        day: dateStr,
        hours: hoursForThisDay,
      })

      hoursRemaining -= hoursForThisDay
    }
  }

  return manAuthDays
}

/**
 * Determines which OSP data should be used based on the absence type
 * Sickness absences use daily OSP values, non-sickness absences get no OSP
 * @param absenceCategoryId The absence category (Sickness, NonSickness, etc.)
 * @param dailyOSP Dictionary of daily OSP values
 * @returns The appropriate OSP dictionary for the absence type
 */
export const getOSPForAbsenceType = (
  absenceCategoryId: number,
  dailyOSP: DailyOSPDictionary
): DailyOSPDictionary => absenceCategoryId === AbsenceTypesEnum.Sickness ? dailyOSP : {}

export const isPayrollNoticeAbsence = (absenceTypeId: number | undefined) =>
  absenceTypeId === AbsenceReasonTypesEnum.Maternity ||
  absenceTypeId === AbsenceReasonTypesEnum.Paternity ||
  absenceTypeId === AbsenceReasonTypesEnum.JuryService ||
  absenceTypeId === AbsenceReasonTypesEnum.FosterFriendlyLeave
export const isCompassionateOtherPartnerChildUnwellHeader = (absenceTypeId: number | undefined) =>
  absenceTypeId === AbsenceReasonTypesEnum.Other ||
  absenceTypeId === AbsenceReasonTypesEnum.Compassionate ||
  absenceTypeId === AbsenceReasonTypesEnum.PartnerChildDependantNotWell

export const getCompassionateHeaderText = (typeId: number | undefined) =>
  COMPASSIONATE_HEADERS[typeId as keyof typeof COMPASSIONATE_HEADERS] ||
  'Compassionate Leave is unpaid as per employee handbook'

export const getHeaderText = (absenceTypeId: number | undefined) => {
  if (isCompassionateOtherPartnerChildUnwellHeader(absenceTypeId)) {
    return getCompassionateHeaderText(absenceTypeId)
  }
  if (isPayrollNoticeAbsence(absenceTypeId)) {
    return absenceFormLabels.MaternityPaternityJuryServiceHeader
  }
  return absenceFormLabels.PayApprovalHeader
}

export interface AbsenceValidationResult {
  isExceeded: boolean
  maxDays: number
}

export const isAbsenceDurationExceeded = (
  absenceTypeId: number,
  absenceCategoryId: number,
  selectedDays: AbsenceDayRequest[] | null
): AbsenceValidationResult => {
  const defaultResult: AbsenceValidationResult = {
    isExceeded: false,
    maxDays: 1,
  }

  if (absenceCategoryId !== AbsenceTypesEnum.NonSickness) {
    return defaultResult
  }

  if (!selectedDays || selectedDays.length === 0) {
    return defaultResult
  }

  let maxDays: number

  switch (absenceTypeId) {
    case AbsenceReasonTypesEnum.Bereavement:
      maxDays = 3
      break
    case AbsenceReasonTypesEnum.FuneralAttendance:
      maxDays = 1
      break
    default:
      return defaultResult
  }

  // Count only the checked days that the user has actually selected
  const totalDays = selectedDays.filter(day => day.checked).length

  return {
    isExceeded: totalDays > maxDays,
    maxDays,
  }
}

export const isAbsenceDurationExceededWithExtension = (
  absenceTypeId: number,
  absenceCategoryId: number,
  selectedDays: AbsenceDayRequest[] | null,
  originalStartDate?: string,
  originalAbsenceDaysCount?: number
): AbsenceValidationResult => {
  const defaultResult: AbsenceValidationResult = {
    isExceeded: false,
    maxDays: 1,
  }

  if (absenceCategoryId !== AbsenceTypesEnum.NonSickness) {
    return defaultResult
  }

  if (!selectedDays || selectedDays.length === 0) {
    return defaultResult
  }

  let maxDays: number

  switch (absenceTypeId) {
    case AbsenceReasonTypesEnum.Bereavement:
      maxDays = 3

      break
    case AbsenceReasonTypesEnum.FuneralAttendance:
      maxDays = 1
      if (originalStartDate) {
        return {
          isExceeded: true,
          maxDays,
        }
      }
      break
    default:
      return defaultResult
  }

  // Count extension days (checked days from selectedDays)
  const extensionDays = selectedDays.filter(day => day.checked).length

  // Count original absence days (default to 0 if not provided for backward compatibility)
  const originalDays = originalAbsenceDaysCount || 0

  // Total absence duration = original days + extension days
  const totalAbsenceDays = originalDays + extensionDays

  const result = {
    isExceeded: totalAbsenceDays > maxDays,
    maxDays,
  }

  return result
}
